// Which sign-up path a visitor gets. The backend decides from the visitor's
// country (GET /visitor-region/): "sales" regions (India, Pakistan, Bangladesh)
// are sales-led, so they book a demo and only existing accounts sign in; every
// other region is "self_serve" and can create a recruiter account and see
// pricing. While the answer is pending, and on any failure with no cached
// answer, callers render the self-serve surface; only an explicit "sales"
// answer from the server switches a visitor to the sales-led one.
import { useEffect, useState } from "react";

import { API_URL } from "@/components/config/config";
import { getWebsiteTrackingContext, trackWebsiteAction } from "@/lib/analytics/website";

export type VisitorRegion = "sales" | "self_serve";
type ResolvedRegion = VisitorRegion;

const STORAGE_KEY = "aiia:visitor-region";
const REPORTED_KEY = "aiia:visitor-region-reported";
const FETCH_TIMEOUT_MS = 5000;

// One lookup per page load, shared by every component that asks.
let lookup: Promise<ResolvedRegion | null> | null = null;
let resolvedThisLoad: ResolvedRegion | null = null;

const asRegion = (value: unknown): ResolvedRegion | null =>
  value === "sales" || value === "self_serve" ? value : null;

function readCached(): ResolvedRegion | null {
  try {
    return asRegion(window.sessionStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeCached(region: ResolvedRegion): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, region);
  } catch {
    // Storage can be blocked; the answer is simply fetched again next load.
  }
}

// Tells the website report which sign-up path this browser got, once per
// session ("unknown" when the lookup failed). The country needs no field here:
// the events endpoint stamps every event with the country of the sender's IP,
// the same lookup the region comes from. Nothing is recorded without analytics
// consent, and then it is tried again on the next lookup.
function reportRegion(region: ResolvedRegion | null): void {
  const answer = region ?? "unknown";
  if (!getWebsiteTrackingContext()) return;
  try {
    if (window.sessionStorage.getItem(REPORTED_KEY) === answer) return;
    window.sessionStorage.setItem(REPORTED_KEY, answer);
  } catch {
    // Storage can be blocked; the event may then repeat on a later page.
  }
  trackWebsiteAction("product_event", { target: "region_resolved", metric: answer });
}

// null means the lookup failed.
async function fetchRegion(): Promise<ResolvedRegion | null> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT_MS);
  try {
    const response = await fetch(`${API_URL}/visitor-region/`, { signal: controller.signal });
    if (!response.ok) return null;
    const region = asRegion((await response.json())?.region);
    if (region) writeCached(region);
    return region;
  } catch {
    // A failed lookup is not cached, so the next page load asks again.
    return null;
  } finally {
    clearTimeout(timer);
  }
}

function loadRegion(): Promise<ResolvedRegion | null> {
  if (!lookup) {
    lookup = fetchRegion().then((region) => {
      if (region) resolvedThisLoad = region;
      reportRegion(region);
      return region;
    });
  }
  return lookup;
}

export function isSelfServe(region: VisitorRegion): boolean {
  return region === "self_serve";
}

export function useVisitorRegion(): VisitorRegion {
  const [region, setRegion] = useState<VisitorRegion>(
    () => resolvedThisLoad ?? readCached() ?? "self_serve",
  );
  // A cached answer renders at once; the lookup still runs once per page load
  // and corrects it if the visitor's region changed (a VPN, a trip).
  useEffect(() => {
    let live = true;
    loadRegion().then((answer) => {
      // A failed lookup keeps the current answer (cached, or self-serve).
      if (live && answer) setRegion(answer);
    });
    return () => {
      live = false;
    };
  }, []);
  return region;
}
